import React, { forwardRef, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Q_AND_A_SIDES,
  Question,
} from 'router/subrouters/DataRoom/models/QAndA';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import AskQuestionDrawer from '../AskQuestionDrawer/AskQuestionDrawer.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { useNavigate } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';
import { getQABuySideParams } from '../../../QAndARoleFilter/data';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import { QuestionSubmissionInitiatedEventProps } from 'models/MixpanelEvents';
import { richTextJsonToPlainText } from 'components/RichTextEditorField/utils';

type WriteQuestionDrawerProps = {
  selectedForQAndADrawer?: DataRoomNode;
  onSubmitSuccess?: () => void;
  onClose?: () => void;
};

const WriteQuestionDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  WriteQuestionDrawerProps
> = ({ selectedForQAndADrawer, onSubmitSuccess, onClose }, ref) => {
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getMixpanelProps = useGetMixpanelProps();

  const onQuestionSubmit = useCallback(
    async (values: Question, relatedDocument: string) => {
      try {
        const {
          otherSubject,
          subject,
          content: questionContent,
          priority: questionPriority,
        } = values;

        mixpanelService.questionSubmissionInitiated(
          getMixpanelProps({
            questionContent: richTextJsonToPlainText(questionContent),
            questionPriority,
            relatedDocument,
            questionSubject: subject === 'Other' ? otherSubject : subject,
            posingQuestionSide: Q_AND_A_SIDES.BUY,
          }) as QuestionSubmissionInitiatedEventProps,
        );

        const { data: question } =
          await dataRoomApi.questionsAndAnswers.askAQuestion(
            dataRoomId,
            values,
            relatedDocument,
          );
        onSubmitSuccess?.();

        navigate(
          `${getDataRoomRoute(
            dataRoomId,
            'questions/questions-and-answers',
          )}?$or=${getQABuySideParams(question?.status)}`,
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [dataRoomId, getMixpanelProps, navigate, onSubmitSuccess, popServerError],
  );

  return (
    <AskQuestionDrawer
      title={t('askAQuestion')}
      selectedForQAndADrawer={selectedForQAndADrawer}
      onSubmit={onQuestionSubmit}
      onClose={onClose}
      ref={ref}
      initialValues={{ priority: 'Medium' }}
    />
  );
};

export default forwardRef(WriteQuestionDrawer);
